import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Award, Clock3, UserRound } from "lucide-react";
import api from "../lib/api";
import { useSiteContent } from "../hooks/useSiteContent";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";

const DEFAULT_FILTERS = ["All", "Knife & Weapon Detection", "Explosives & Narcotics", "Narcotics & Contraband", "Event & Venue Sweeps"];

export default function Dogs() {
  const content = useSiteContent();
  const pg = content?.pages?.dogs || {};
  const [dogs, setDogs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/dogs")
      .then((r) => {
        setDogs(r.data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const specialties = ["All", ...Array.from(new Set(dogs.map((d) => d.specialty).filter(Boolean)))];
  const filters = specialties.length > 1 ? specialties : DEFAULT_FILTERS;
  const visible = filter === "All" ? dogs : dogs.filter((d) => d.specialty === filter);

  return (
    <main className="bg-brand-obsidian pt-28 sm:pt-32" data-testid="dogs-page">
      <section className="px-4 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.eyebrow || "The Team"}</Eyebrow>
            <h1 className="mt-4 font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight">
              {pg.title || "Meet Our"} <span className="text-brand-crimson">{pg.title_accent || "Canine Specialists"}</span>
            </h1>
            <p className="mt-4 max-w-2xl text-slate-400 leading-relaxed">
              {pg.intro || "Every dog at Quest K9's is raised and trained in-house, socialised from puppyhood, and paired with one accredited handler for their whole career."}
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="dogs-filter-tabs">
            {filters.map((f) => (
              <button
                key={f}
                data-testid={`dogs-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`font-eyebrow text-[11px] uppercase tracking-[0.18em] font-bold px-4 py-2 rounded-md border transition-all duration-300 ${
                  filter === f
                    ? "bg-brand-crimson border-brand-crimson text-white"
                    : "border-brand-line text-white/60 hover:border-brand-crimson/60 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="mt-10 pb-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {visible.map((dog, i) => (
              <Reveal key={dog.id || dog.name} delay={i * 0.07}>
                <button
                  onClick={() => setActive(dog)}
                  data-testid={`dog-card-${(dog.name || "dog").toLowerCase()}`}
                  className="group relative w-full text-left overflow-hidden rounded-lg border border-brand-line bg-brand-surface transition-all duration-300 hover:border-brand-crimson/70 hover:shadow-[0_0_40px_rgba(200,16,46,0.18)]"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={dog.photo}
                      alt={`${dog.name} - ${dog.breed}`}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-obsidian via-transparent to-transparent" />
                    <span className="absolute top-3 left-3 bg-brand-crimson text-white font-eyebrow text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-md">
                      {dog.specialty}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-xl font-bold text-white">{dog.name}</h3>
                        <p className="mt-1 text-sm text-slate-400">{dog.breed}</p>
                      </div>
                      {dog.specialty === "Knife & Weapon Detection" && (
                        <img
                          src="/knife-detection.png"
                          alt="Knife Detection Dogs accredited"
                          className="h-12 w-auto rounded bg-white p-1 border border-white/20 shrink-0"
                          data-testid={`dog-knife-logo-${(dog.name || "").toLowerCase()}`}
                        />
                      )}
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-xs text-white/50">
                      <Clock3 className="h-3.5 w-3.5 text-brand-crimson" /> {dog.years_of_service}
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
            {loaded && visible.length === 0 && (
              <p className="col-span-full text-center text-white/50 py-16" data-testid="dogs-empty">{pg.empty || "No dogs listed yet - check back soon."}</p>
            )}
          </div>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-lg" data-testid="dog-detail-modal">
          {active && (
            <div>
              <motion.img
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                src={active.photo}
                alt={active.name}
                className="h-64 w-full object-cover rounded-md"
              />
              <DialogHeader className="text-left mt-5">
                <div className="flex items-baseline justify-between gap-4">
                  <DialogTitle className="font-display text-2xl font-bold text-white" data-testid="dog-modal-name">{active.name}</DialogTitle>
                  <Badge className="bg-brand-crimson hover:bg-brand-crimson text-white" data-testid="dog-modal-specialty">{active.specialty}</Badge>
                </div>
                <DialogDescription className="text-slate-400 text-sm mt-1">{active.breed}</DialogDescription>
              </DialogHeader>
              <p className="mt-4 text-sm text-slate-300 leading-relaxed" data-testid="dog-modal-bio">{active.description}</p>
              <div className="mt-5 flex flex-wrap gap-4 text-xs text-white/60">
                <span className="flex items-center gap-2"><Award className="h-4 w-4 text-brand-crimson" /> {active.certification}</span>
                <span className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-brand-crimson" /> {active.years_of_service}</span>
                <span className="flex items-center gap-2"><UserRound className="h-4 w-4 text-brand-crimson" /> Dedicated Handler</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
